// import React, { useRef, useState } from 'react';
// import Text from './TextComponent';

// type ImageUploaderProps = {
//   size?: number;
//   defaultImage?: string;
//   maxSizeMB?: number;
//   uploadUrl: string;
//   showSpinner?: boolean;
// };

// const ImageUploader: React.FC<ImageUploaderProps> = ({
//   size = 120,
//   defaultImage = '',
//   maxSizeMB = 2,
//   uploadUrl,
//   showSpinner = true,
// }) => {
//   const fileInputRef = useRef<HTMLInputElement>(null);
//   const [imageSrc, setImageSrc] = useState<string>(defaultImage);
//   const [error, setError] = useState<string | null>(null);
//   const [isUploading, setIsUploading] = useState<boolean>(false);
//   const [uploadAttempted, setUploadAttempted] = useState<boolean>(false);

//   const supportedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];

//   const handleImageClick = () => {
//     fileInputRef.current?.click();
//   };

//   const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
//     const file = e.target.files?.[0];
//     if (!file) return;

//     // Reset state for new attempt
//     setError(null);
//     setUploadAttempted(false);

//     if (!supportedTypes.includes(file.type)) {
//       setError('Only PNG, JPG, JPEG, or SVG files are allowed');
//       setUploadAttempted(true);
//       return;
//     }

//     const maxSizeBytes = maxSizeMB * 1024 * 1024;
//     if (file.size > maxSizeBytes) {
//       setError(`File exceeds ${maxSizeMB}MB limit`);
//       setUploadAttempted(true);
//       return;
//     }

//     const reader = new FileReader();
//     reader.onloadend = async () => {
//       const base64Image = reader.result as string;
//       setImageSrc(base64Image);

//       try {
//         setIsUploading(true);

//         const formData = new FormData();
//         formData.append('image', file);

//         const response = await fetch(uploadUrl, {
//           method: 'POST',
//           body: formData,
//         });

//         if (!response.ok) throw new Error('Upload failed');

//         setUploadAttempted(true);
//       } catch (err) {
//         console.error(err);
//         setError('Failed to upload image');
//         setUploadAttempted(true);
//       } finally {
//         setIsUploading(false);
//       }
//     };

//     reader.readAsDataURL(file);
//   };

//   const shouldShowPlus = !imageSrc && !uploadAttempted;

//   return (
//     <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
//       <div
//         style={{
//           width: size,
//           height: size,
//           borderRadius: '50%',
//           overflow: 'hidden',
//           cursor: 'pointer',
//           border: '2px solid #ccc',
//           display: 'flex',
//           alignItems: 'center',
//           justifyContent: 'center',
//           backgroundColor: '#f5f5f5',
//           position: 'relative',
//         }}
//         onClick={handleImageClick}
//       >
//         {showSpinner && isUploading && <div style={spinnerStyle} />}

//         {!isUploading && imageSrc && (
//           <img
//             src={imageSrc}
//             alt="Uploaded"
//             style={{ width: '100%', height: '100%', objectFit: 'cover' }}
//           />
//         )}

//         {shouldShowPlus && (
//           <div style={plusIconStyle}>+</div>
//         )}

//         <input
//           type="file"
//           accept=".png,.jpg,.jpeg,.svg"
//           ref={fileInputRef}
//           onChange={handleFileChange}
//           style={{ display: 'none' }}
//         />
//       </div>

//       {error && (
//         <Text
//             text={error}
//             fontSize="14px"
//             fontWeight="500"
//             lineHeight="32px"
//             color="red"
//             textAlign="center"
//         />
//       )}
//     </div>
//   );
// };

// const spinnerStyle: React.CSSProperties = {
//   width: '40px',
//   height: '40px',
//   border: '4px solid #ccc',
//   borderTop: '4px solid #8b2cd7',
//   borderRadius: '50%',
//   animation: 'spin 1s linear infinite',
//   position: 'absolute',
// };

// const plusIconStyle: React.CSSProperties = {
//   fontSize: '40px',
//   color: '#999',
//   position: 'absolute',
//   top: '50%',
//   left: '50%',
//   transform: 'translate(-50%, -50%)',
//   pointerEvents: 'none',
// };

// export default ImageUploader;
import React, { useRef, useState } from 'react';
import Text from './TextComponent';
import { uploadImageToCloudinarySigned } from '../../api/cloudinaryApi';

type ImageUploaderProps = {
  size?: number;
  defaultImage?: string;
  maxSizeMB?: number;
  showSpinner?: boolean;
  onUploadSuccess?: (imageUrl: string) => void; // Pass final URL to parent
};

const ImageUploader: React.FC<ImageUploaderProps> = ({
  size = 120,
  defaultImage = '',
  maxSizeMB = 2,
  showSpinner = true,
  onUploadSuccess,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageSrc, setImageSrc] = useState<string>(defaultImage);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadAttempted, setUploadAttempted] = useState<boolean>(false);

  const supportedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setUploadAttempted(false);

    if (!supportedTypes.includes(file.type)) {
      setError('Only PNG, JPG, JPEG, or SVG files are allowed');
      setUploadAttempted(true);
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File exceeds ${maxSizeMB}MB limit`);
      setUploadAttempted(true);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageSrc(reader.result as string); // local preview
    };
    reader.readAsDataURL(file);

    try {
      setIsUploading(true);

      // Upload to Cloudinary with signed axios helper
      const result = await uploadImageToCloudinarySigned(file);
      if (result.error || !result.data) {
        throw new Error(result.error ?? 'Image upload failed');
      }

      const imageUrl = result.data.secure_url;
      setUploadAttempted(true);
      onUploadSuccess?.(imageUrl); // Pass URL to parent
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
      setUploadAttempted(true);
    } finally {
      setIsUploading(false);
    }
  };

  const shouldShowPlus = !imageSrc && !uploadAttempted;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          overflow: 'hidden',
          cursor: 'pointer',
          border: '2px solid #ccc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          position: 'relative',
        }}
        onClick={handleImageClick}
      >
        {showSpinner && isUploading && <div style={spinnerStyle} />}
        {!isUploading && imageSrc && (
          <img
            src={imageSrc}
            alt="Uploaded"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        {shouldShowPlus && <div style={plusIconStyle}>+</div>}
        <input
          type="file"
          accept=".png,.jpg,.jpeg,.svg"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>
      {error && (
        <Text
          text={error}
          fontSize="14px"
          fontWeight="500"
          lineHeight="32px"
          color="red"
          textAlign="center"
        />
      )}
    </div>
  );
};

const spinnerStyle: React.CSSProperties = {
  width: '40px',
  height: '40px',
  border: '4px solid #ccc',
  borderTop: '4px solid var(--primary)',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  position: 'absolute',
};

const plusIconStyle: React.CSSProperties = {
  fontSize: '40px',
  color: '#999',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',
};

export default ImageUploader;
